import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminAdvanced.css';

function CasePerformance() {
  const [performanceData, setPerformanceData] = useState({
    categoriesOverTime: [],
    completionRates: [],
    topSectors: [],
    scoreDistribution: {},
    monthlyActivity: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeframe, setTimeframe] = useState('6m'); // 1m, 3m, 6m, 1y, all

  useEffect(() => {
    fetchPerformanceData();
  }, [timeframe]);

  const fetchPerformanceData = async () => {
    setIsLoading(true);
    try {
      // Utiliser le nouvel endpoint
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/case-performance`);
      const data = response.data;
      
      setPerformanceData(data);
      
      setError(null);
    } catch (err) {
      console.error("Erreur lors de la récupération des données de performance:", err);
      setError("Impossible de charger les données de performance. Utilisation des données de démonstration.");
      
      // Utiliser des données de démonstration en cas d'erreur
      setPerformanceData({
        categoriesOverTime: [
          {
            category: "environmental",
            data: [
              {month: "Jan", value: 5.2},
              {month: "Fév", value: 5.7},
              {month: "Mar", value: 6.1},
              {month: "Avr", value: 6.3},
              {month: "Mai", value: 6.5},
              {month: "Juin", value: 6.8}
            ]
          },
          {
            category: "social",
            data: [
              {month: "Jan", value: 4.1},
              {month: "Fév", value: 4.2},
              {month: "Mar", value: 4.5},
              {month: "Avr", value: 4.8},
              {month: "Mai", value: 5.2},
              {month: "Juin", value: 5.5}
            ]
          },
          {
            category: "modernization",
            data: [
              {month: "Jan", value: 6.7},
              {month: "Fév", value: 7.1},
              {month: "Mar", value: 7.3},
              {month: "Avr", value: 7.5},
              {month: "Mai", value: 7.8},
              {month: "Juin", value: 8.0}
            ]
          }
        ],
        completionRates: [
          {period: "Dernier mois", rate: 65},
          {period: "3 derniers mois", rate: 72},
          {period: "6 derniers mois", rate: 68}
        ],
        topSectors: [
          {sector: "Automobile", count: 24, percentage: 24},
          {sector: "Aérospatiale", count: 18, percentage: 18},
          {sector: "à‰lectronique", count: 16, percentage: 16},
          {sector: "Chimie", count: 15, percentage: 15},
          {sector: "Pharmaceutique", count: 14, percentage: 14},
          {sector: "Autres", count: 13, percentage: 13}
        ],
        scoreDistribution: {
          high: 30,
          medium: 50,
          low: 20
        },
        monthlyActivity: [
          {month: "Jan", newCases: 12, updatedCases: 8},
          {month: "Fév", newCases: 15, updatedCases: 10},
          {month: "Mar", newCases: 18, updatedCases: 12},
          {month: "Avr", newCases: 22, updatedCases: 15},
          {month: "Mai", newCases: 19, updatedCases: 14},
          {month: "Juin", newCases: 25, updatedCases: 18}
        ]
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction pour rendre le graphique de tendance des catégories
  const renderCategoryTrends = () => {
    const categoryNames = {
      'environmental': 'Environnement',
      'social': 'Social',
      'modernization': 'Modernisation',
      'leadTime': 'Délai',
      'cost': 'Coà»t',
      'quality': 'Qualité',
      'clientConsumer': 'Client/Consommateur'
    };
    
    if (!performanceData.categoriesOverTime || performanceData.categoriesOverTime.length === 0) {
      return <div className="empty-chart-message">Aucune donnée disponible</div>;
    }
    
    // Vérifier si les données sont au format attendu (avec data)
    const isNewFormat = performanceData.categoriesOverTime[0] && 'data' in performanceData.categoriesOverTime[0];
    
    if (isNewFormat) {
      // Obtenir la liste des mois uniques de toutes les catégories
      const allMonths = new Set();
      performanceData.categoriesOverTime.forEach(category => {
        if (category.data) {
          category.data.forEach(item => {
            allMonths.add(item.month);
          });
        }
      });
      
      const months = Array.from(allMonths);
      
      return (
        <div className="trends-chart">
          <div className="chart-legend">
            {performanceData.categoriesOverTime.map((category, index) => (
              <div key={index} className="legend-item">
                <div className="legend-color" style={{ backgroundColor: getCategoryColor(category.category) }}></div>
                <div className="legend-label">{categoryNames[category.category] || category.category}</div>
              </div>
            ))}
          </div>
          
          <div className="line-chart">
            <div className="line-chart-grid">
              {months.map((month, monthIndex) => (
                <div key={monthIndex} className="grid-line"></div>
              ))}
            </div>
            
            {performanceData.categoriesOverTime.map((category, categoryIndex) => {
              if (!category.data) return null;
              
              const points = category.data.map((item, i) => {
                const monthIndex = months.indexOf(item.month);
                // Positionner les points proportionnellement
                const x = `${(monthIndex / (months.length - 1)) * 100}%`;
                // Normaliser la valeur entre 0 et 100%
                const maxValue = Math.max(...performanceData.categoriesOverTime.flatMap(c => c.data ? c.data.map(d => d.value) : []));
                const y = `${100 - (item.value / maxValue) * 80}%`; // 80% pour laisser de l'espace en haut/bas
                
                return { x, y, value: item.value, month: item.month };
              });
              
              // Construire le chemin de la ligne
              const pathData = points.map((point, i) => 
                `${i === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
              ).join(' ');
              
              return (
                <React.Fragment key={categoryIndex}>
                  <svg className="line-chart-paths" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <path 
                      d={pathData} 
                      fill="none" 
                      stroke={getCategoryColor(category.category)} 
                      strokeWidth="2" 
                    />
                  </svg>
                  
                  {points.map((point, pointIndex) => (
                    <div 
                      key={pointIndex}
                      className="data-point"
                      style={{ 
                        left: point.x, 
                        top: point.y,
                        backgroundColor: getCategoryColor(category.category)
                      }}
                      title={`${categoryNames[category.category] || category.category}: ${point.value} (${point.month})`}
                    ></div>
                  ))}
                </React.Fragment>
              );
            })}
            
            <div className="chart-axis x-axis">
              {months.map((month, index) => (
                <div key={index} className="axis-label" style={{ left: `${(index / (months.length - 1)) * 100}%` }}>
                  {month}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    } else {
      // Format ancien (comme dans les données de démonstration)
      const months = performanceData.categoriesOverTime.map(item => item.month);
      const categories = Object.keys(performanceData.categoriesOverTime[0]).filter(key => key !== 'month');
      
      return (
        <div className="trends-chart">
          <div className="chart-legend">
            {categories.map((category, index) => (
              <div key={index} className="legend-item">
                <div className="legend-color" style={{ backgroundColor: getCategoryColor(category) }}></div>
                <div className="legend-label">{categoryNames[category] || category}</div>
              </div>
            ))}
          </div>
          
          <div className="line-chart">
            <div className="line-chart-grid">
              {months.map((month, monthIndex) => (
                <div key={monthIndex} className="grid-line"></div>
              ))}
            </div>
            
            {categories.map((category, categoryIndex) => {
              const points = performanceData.categoriesOverTime.map((item, i) => {
                // Positionner les points proportionnellement
                const x = `${(i / (performanceData.categoriesOverTime.length - 1)) * 100}%`;
                // Normaliser la valeur entre 0 et 100%
                const maxValue = Math.max(...performanceData.categoriesOverTime.flatMap(item => 
                  categories.map(cat => item[cat])
                ));
                const y = `${100 - (item[category] / maxValue) * 80}%`; // 80% pour laisser de l'espace en haut/bas
                
                return { x, y, value: item[category], month: item.month };
              });
              
              // Construire le chemin de la ligne
              const pathData = points.map((point, i) => 
                `${i === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
              ).join(' ');
              
              return (
                <React.Fragment key={categoryIndex}>
                  <svg className="line-chart-paths" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <path 
                      d={pathData} 
                      fill="none" 
                      stroke={getCategoryColor(category)} 
                      strokeWidth="2" 
                    />
                  </svg>
                  
                  {points.map((point, pointIndex) => (
                    <div 
                      key={pointIndex}
                      className="data-point"
                      style={{ 
                        left: point.x, 
                        top: point.y,
                        backgroundColor: getCategoryColor(category)
                      }}
                      title={`${categoryNames[category] || category}: ${point.value} (${point.month})`}
                    ></div>
                  ))}
                </React.Fragment>
              );
            })}
            
            <div className="chart-axis x-axis">
              {months.map((month, index) => (
                <div key={index} className="axis-label" style={{ left: `${(index / (months.length - 1)) * 100}%` }}>
                  {month}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }
  };

  // Fonction pour obtenir une couleur par catégorie
  const getCategoryColor = (category) => {
    const colors = {
      'environmental': '#4CAF50',
      'social': '#2196F3',
      'modernization': '#9C27B0',
      'leadTime': '#FF9800',
      'cost': '#F44336',
      'quality': '#00BCD4',
      'clientConsumer': '#795548'
    };
    
    return colors[category] || '#888888';
  };

  // Fonction pour rendre les taux de complétion
  const renderCompletionRates = () => {
    if (!performanceData.completionRates) {
      return <div className="empty-chart-message">Aucune donnée disponible</div>;
    }
    
    // Vérifier si completionRates est un tableau ou un objet
    const isArray = Array.isArray(performanceData.completionRates);
    
    if (isArray) {
      // Format sous forme de tableau (comme dans les données mises à  jour)
      if (performanceData.completionRates.length === 0) {
        return <div className="empty-chart-message">Aucune donnée disponible</div>;
      }
      
      return (
        <div className="completion-rates">
          {performanceData.completionRates.map((item, index) => (
            <div key={index} className="completion-rate-item">
              <div className="rate-label">{item.period}</div>
              <div className="rate-bar-container">
                <div 
                  className="rate-bar" 
                  style={{ width: `${item.rate}%` }}
                ></div>
                <span className="rate-value">{item.rate}%</span>
              </div>
            </div>
          ))}
        </div>
      );
    } else {
      // Format sous forme d'objet (comme dans les anciennes données)
      const { total, completed, rate } = performanceData.completionRates;
      
      if (!total && !completed && !rate) {
        return <div className="empty-chart-message">Aucune donnée disponible</div>;
      }
      
      return (
        <div className="completion-rates">
          <div className="completion-rate-item">
            <div className="rate-label">Taux global</div>
            <div className="rate-bar-container">
              <div 
                className="rate-bar" 
                style={{ width: `${rate}%` }}
              ></div>
              <span className="rate-value">{rate}%</span>
            </div>
          </div>
          <div className="completion-stats">
            <div>Cas complétés: {completed} sur {total}</div>
          </div>
        </div>
      );
    }
  };

  // Fonction pour rendre les meilleurs secteurs
  const renderTopSectors = () => {
    if (!performanceData.topSectors || performanceData.topSectors.length === 0) {
      return <div className="empty-chart-message">Aucune donnée disponible</div>;
    }
    
    return (
      <div className="top-sectors">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Secteur</th>
              <th>Nombre de cas</th>
              <th>Score moyen</th>
            </tr>
          </thead>
          <tbody>
            {performanceData.topSectors.map((item, index) => (
              <tr key={index}>
                <td>{item.sector}</td>
                <td>{item.count}</td>
                <td>
                  <div className="score-bar-mini-container">
                    <div 
                      className="score-bar-mini" 
                      style={{ width: `${item.percentage}%` }}
                    ></div>
                    <span>{item.percentage}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Fonction pour rendre la distribution des scores
  const renderScoreDistribution = () => {
    if (!performanceData.scoreDistribution) {
      return <div className="empty-chart-message">Aucune donnée disponible</div>;
    }
    
    // Vérifier si les données sont au nouveau format (objet avec high, medium, low)
    const isNewFormat = 'high' in performanceData.scoreDistribution && 
                       'medium' in performanceData.scoreDistribution && 
                       'low' in performanceData.scoreDistribution;
    
    if (isNewFormat) {
      const { high, medium, low } = performanceData.scoreDistribution;
      const total = high + medium + low;
      
      if (total === 0) {
        return <div className="empty-chart-message">Aucune donnée disponible</div>;
      }
      
      return (
        <div className="score-distribution">
          <div className="donut-chart-container">
            <div className="donut-chart">
              <svg viewBox="0 0 42 42" className="donut">
                <circle 
                  className="donut-hole" 
                  cx="21" 
                  cy="21" 
                  r="15.91549430918954" 
                  fill="#fff"
                ></circle>
                
                <circle 
                  className="donut-ring" 
                  cx="21" 
                  cy="21" 
                  r="15.91549430918954" 
                  fill="transparent" 
                  stroke="#d2d3d4" 
                  strokeWidth="3"
                ></circle>
                
                <circle 
                  className="donut-segment donut-segment-high" 
                  cx="21" 
                  cy="21" 
                  r="15.91549430918954" 
                  fill="transparent" 
                  stroke="#4CAF50" 
                  strokeWidth="3" 
                  strokeDasharray={`${(high / total) * 100} ${100 - (high / total) * 100}`} 
                  strokeDashoffset="25"
                ></circle>
                
                <circle 
                  className="donut-segment donut-segment-medium" 
                  cx="21" 
                  cy="21" 
                  r="15.91549430918954" 
                  fill="transparent" 
                  stroke="#FFC107" 
                  strokeWidth="3" 
                  strokeDasharray={`${(medium / total) * 100} ${100 - (medium / total) * 100}`} 
                  strokeDashoffset={`${25 - (high / total) * 100}`}
                ></circle>
                
                <circle 
                  className="donut-segment donut-segment-low" 
                  cx="21" 
                  cy="21" 
                  r="15.91549430918954" 
                  fill="transparent" 
                  stroke="#F44336" 
                  strokeWidth="3" 
                  strokeDasharray={`${(low / total) * 100} ${100 - (low / total) * 100}`} 
                  strokeDashoffset={`${25 - ((high + medium) / total) * 100}`}
                ></circle>
              </svg>
            </div>
            
            <div className="donut-chart-legend">
              <div className="legend-item">
                <div className="legend-color" style={{ backgroundColor: '#4CAF50' }}></div>
                <div className="legend-label">Score élevé</div>
                <div className="legend-value">{high} ({Math.round((high / total) * 100)}%)</div>
              </div>
              <div className="legend-item">
                <div className="legend-color" style={{ backgroundColor: '#FFC107' }}></div>
                <div className="legend-label">Score moyen</div>
                <div className="legend-value">{medium} ({Math.round((medium / total) * 100)}%)</div>
              </div>
              <div className="legend-item">
                <div className="legend-color" style={{ backgroundColor: '#F44336' }}></div>
                <div className="legend-label">Score faible</div>
                <div className="legend-value">{low} ({Math.round((low / total) * 100)}%)</div>
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      // Format ancien (tableau de {score, count})
      if (!Array.isArray(performanceData.scoreDistribution) || performanceData.scoreDistribution.length === 0) {
        return <div className="empty-chart-message">Aucune donnée disponible</div>;
      }
      
      // Calculer les totaux par catégorie
      const totalCount = performanceData.scoreDistribution.reduce((sum, item) => sum + item.count, 0);
      const highScores = performanceData.scoreDistribution
        .filter(item => item.score >= 8)
        .reduce((sum, item) => sum + item.count, 0);
      const mediumScores = performanceData.scoreDistribution
        .filter(item => item.score >= 4 && item.score < 8)
        .reduce((sum, item) => sum + item.count, 0);
      const lowScores = performanceData.scoreDistribution
        .filter(item => item.score < 4)
        .reduce((sum, item) => sum + item.count, 0);
      
      if (totalCount === 0) {
        return <div className="empty-chart-message">Aucune donnée disponible</div>;
      }
      
      return (
        <div className="score-distribution">
          <div className="donut-chart-container">
            <div className="donut-chart">
              <svg viewBox="0 0 42 42" className="donut">
                <circle 
                  className="donut-hole" 
                  cx="21" 
                  cy="21" 
                  r="15.91549430918954" 
                  fill="#fff"
                ></circle>
                
                <circle 
                  className="donut-ring" 
                  cx="21" 
                  cy="21" 
                  r="15.91549430918954" 
                  fill="transparent" 
                  stroke="#d2d3d4" 
                  strokeWidth="3"
                ></circle>
                
                <circle 
                  className="donut-segment donut-segment-high" 
                  cx="21" 
                  cy="21" 
                  r="15.91549430918954" 
                  fill="transparent" 
                  stroke="#4CAF50" 
                  strokeWidth="3" 
                  strokeDasharray={`${(highScores / totalCount) * 100} ${100 - (highScores / totalCount) * 100}`} 
                  strokeDashoffset="25"
                ></circle>
                
                <circle 
                  className="donut-segment donut-segment-medium" 
                  cx="21" 
                  cy="21" 
                  r="15.91549430918954" 
                  fill="transparent" 
                  stroke="#FFC107" 
                  strokeWidth="3" 
                  strokeDasharray={`${(mediumScores / totalCount) * 100} ${100 - (mediumScores / totalCount) * 100}`} 
                  strokeDashoffset={`${25 - (highScores / totalCount) * 100}`}
                ></circle>
                
                <circle 
                  className="donut-segment donut-segment-low" 
                  cx="21" 
                  cy="21" 
                  r="15.91549430918954" 
                  fill="transparent" 
                  stroke="#F44336" 
                  strokeWidth="3" 
                  strokeDasharray={`${(lowScores / totalCount) * 100} ${100 - (lowScores / totalCount) * 100}`} 
                  strokeDashoffset={`${25 - ((highScores + mediumScores) / totalCount) * 100}`}
                ></circle>
              </svg>
            </div>
            
            <div className="donut-chart-legend">
              <div className="legend-item">
                <div className="legend-color" style={{ backgroundColor: '#4CAF50' }}></div>
                <div className="legend-label">Score élevé (8-10)</div>
                <div className="legend-value">{highScores} ({Math.round((highScores / totalCount) * 100)}%)</div>
              </div>
              <div className="legend-item">
                <div className="legend-color" style={{ backgroundColor: '#FFC107' }}></div>
                <div className="legend-label">Score moyen (4-7)</div>
                <div className="legend-value">{mediumScores} ({Math.round((mediumScores / totalCount) * 100)}%)</div>
              </div>
              <div className="legend-item">
                <div className="legend-color" style={{ backgroundColor: '#F44336' }}></div>
                <div className="legend-label">Score faible (1-3)</div>
                <div className="legend-value">{lowScores} ({Math.round((lowScores / totalCount) * 100)}%)</div>
              </div>
            </div>
          </div>
        </div>
      );
    }
  };

  // Fonction pour rendre l'activité mensuelle
  const renderMonthlyActivity = () => {
    if (!performanceData.monthlyActivity) {
      return <div className="empty-chart-message">Aucune donnée disponible</div>;
    }
    
    // Vérifier si les données sont au format attendu
    const hasNewAndUpdatedCases = performanceData.monthlyActivity.length > 0 && 
                                 'newCases' in performanceData.monthlyActivity[0] && 
                                 'updatedCases' in performanceData.monthlyActivity[0];
    
    if (hasNewAndUpdatedCases) {
      // Format avec newCases et updatedCases
      if (performanceData.monthlyActivity.length === 0) {
        return <div className="empty-chart-message">Aucune donnée disponible</div>;
      }
      
      // Trouver la valeur maximale pour dimensionner correctement les barres
      const maxValue = Math.max(...performanceData.monthlyActivity.flatMap(item => [item.newCases, item.updatedCases]));
      
      return (
        <div className="monthly-activity">
          <div className="chart-legend">
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#4CAF50' }}></div>
              <div className="legend-label">Nouveaux cas</div>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ backgroundColor: '#2196F3' }}></div>
              <div className="legend-label">Cas mis à  jour</div>
            </div>
          </div>
          
          <div className="bar-chart-grouped">
            {performanceData.monthlyActivity.map((item, index) => (
              <div key={index} className="bar-chart-group">
                <div className="bar-chart-label">{item.month}</div>
                <div className="bar-chart-bars">
                  <div className="bar-chart-bar-container">
                    <div 
                      className="bar-chart-bar bar-new" 
                      style={{ 
                        height: `${(item.newCases / maxValue) * 100}%`,
                        minHeight: item.newCases > 0 ? '4px' : '0'
                      }}
                      title={`${item.newCases} nouveaux cas`}
                    ></div>
                  </div>
                  <div className="bar-chart-bar-container">
                    <div 
                      className="bar-chart-bar bar-updated" 
                      style={{ 
                        height: `${(item.updatedCases / maxValue) * 100}%`,
                        minHeight: item.updatedCases > 0 ? '4px' : '0'
                      }}
                      title={`${item.updatedCases} cas mis à  jour`}
                    ></div>
                  </div>
                </div>
                <div className="bar-chart-values">
                  <span className="bar-value">{item.newCases}</span>
                  <span className="bar-value">{item.updatedCases}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    } else {
      // Format ancien (avec month et count)
      if (performanceData.monthlyActivity.length === 0) {
        return <div className="empty-chart-message">Aucune donnée disponible</div>;
      }
      
      // Trouver la valeur maximale pour dimensionner correctement les barres
      const maxValue = Math.max(...performanceData.monthlyActivity.map(item => item.count));
      
      return (
        <div className="monthly-activity">
          <div className="bar-chart-simple">
            {performanceData.monthlyActivity.map((item, index) => (
              <div key={index} className="bar-chart-item">
                <div className="bar-chart-label">
                  {typeof item.month === 'number' ? `Mois ${item.month}` : item.month}
                </div>
                <div className="bar-chart-bar-container">
                  <div 
                    className="bar-chart-bar" 
                    style={{ 
                      height: `${(item.count / maxValue) * 100}%`,
                      minHeight: item.count > 0 ? '4px' : '0'
                    }}
                    title={`${item.count} cas`}
                  ></div>
                </div>
                <div className="bar-value">{item.count}</div>
              </div>
            ))}
          </div>
        </div>
      );
    }
  };

  return (
    <div className="case-performance-container">
      <div className="performance-header">
        <h2>Performance des Cas</h2>
        
        <div className="timeframe-selector">
          <span>Période:</span>
          <select value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
            <option value="1m">1 mois</option>
            <option value="3m">3 mois</option>
            <option value="6m">6 mois</option>
            <option value="1y">1 an</option>
            <option value="all">Tout</option>
          </select>
        </div>
      </div>
      
      {error && <div className="admin-error">{error}</div>}
      
      {isLoading ? (
        <div className="loading-indicator">Chargement des données de performance...</div>
      ) : (
        <div className="performance-content">
          <div className="performance-card wide-card">
            <h3>à‰volution des scores par catégorie</h3>
            {renderCategoryTrends()}
          </div>
          
          <div className="performance-row">
            <div className="performance-card">
              <h3>Taux de complétion</h3>
              {renderCompletionRates()}
            </div>
            
            <div className="performance-card">
              <h3>Distribution des scores</h3>
              {renderScoreDistribution()}
            </div>
          </div>
          
          <div className="performance-row">
            <div className="performance-card">
              <h3>Top secteurs d'activité</h3>
              {renderTopSectors()}
            </div>
            
            <div className="performance-card">
              <h3>Activité mensuelle</h3>
              {renderMonthlyActivity()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CasePerformance; 